import { PlusCircle } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import FloatingInput from '../FloatingInput.jsx';
import SearchableSelect from '../SearchableSelect.jsx';
import toast from 'react-hot-toast';

export function QualificationDetailsForm({
    employeeData,
    setEmployeeData,
    onSaveSection,
    loading,
    feedback,
    darkMode,
    onCancel,
    onNext,
    isLastTab,
    readOnly = false,
    onSaveTrigger,
}) {
    // Ensure qualifications is always an array
    const formData = {
        ...employeeData,
        qualifications: employeeData.qualifications || []
    };
    
    // State for year validation errors
    const [yearErrors, setYearErrors] = useState({});
    // State for education validation errors
    const [educationErrors, setEducationErrors] = useState({
        educationStatus: '',
        qualifications: []
    });

    const handleInputChange = useCallback((e) => {
        const { name, checked } = e.target;
        // Toggle flags like lessThanPrimary without clearing existing data so that
        // checking/unchecking does not wipe unsaved qualification rows.
        // Data will only be cleared when Update is clicked (in handleSave)
        setEmployeeData(prev => ({ 
            ...prev, 
            [name]: checked,
            qualifications: prev.qualifications,
        }));
        
        // Clear education status error when checkbox is checked
        if (checked) {
            setEducationErrors(prev => ({
                ...prev,
                educationStatus: ''
            }));
        }
    }, [setEmployeeData]);

    const handleNestedInputChange = useCallback((index, field, value) => {
        // Special validation for year of passing
        if (field === 'yearOfPassing') {
            const stringValue = value.toString();
            if (value && stringValue.trim() !== '') {
                // Clear year validation error when value is entered
                setYearErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors[index];
                    return newErrors;
                });
                
                // Validate 4-digit numeric year
                if (!/^\d{4}$/.test(stringValue)) {
                    setYearErrors(prev => ({
                        ...prev,
                        [index]: 'Year must be exactly 4 digits (e.g., 2023)'
                    }));
                }
            } else {
                // Show error only when field is empty
                setYearErrors(prev => ({
                    ...prev,
                    [index]: 'Year of passing is required'
                }));
            }
        }
        
        // Clear specific field errors when user enters valid data
        const stringValue = value.toString();
        if (value && stringValue.trim() !== '') {
            setEducationErrors(prev => ({
                ...prev,
                qualifications: prev.qualifications.map((qualError, i) => {
                    if (i === index) {
                        const newQualError = { ...qualError };
                        delete newQualError[field];
                        return newQualError;
                    }
                    return qualError;
                })
            }));
            
            // Clear education status error when any qualification field has data
            setEducationErrors(prev => ({
                ...prev,
                educationStatus: ''
            }));
        } else {
            // Only validate the specific field that is empty
            setEducationErrors(prev => ({
                ...prev,
                qualifications: prev.qualifications.map((qualError, i) => {
                    if (i === index) {
                        const newQualError = { ...qualError };
                        if (field === 'degree' && (!value || stringValue.trim() === '')) {
                            newQualError.degree = 'Degree is required';
                        } else if (field === 'institution' && (!value || stringValue.trim() === '')) {
                            newQualError.institution = 'Institution is required';
                        } else if (field === 'percentage' && (!value || stringValue.trim() === '')) {
                            newQualError.percentage = 'Percentage/Grade/Class is required';
                        } else if (field === 'specialization' && (!value || stringValue.trim() === '')) {
                            newQualError.specialization = 'Specialization is required';
                        }
                        return newQualError;
                    }
                    return qualError;
                })
            }));
        }
        
        setEmployeeData(prev => ({
            ...prev,
            qualifications: prev.qualifications.map((item, i) =>
                i === index ? { ...item, [field]: value } : item
            ),
        }));
    }, [setEmployeeData]);

    // Validation function to check if qualification data is complete
    const validateQualification = (qualification) => {
        const errors = {};
        if (!qualification.degree || qualification.degree.toString().trim() === '') {
            errors.degree = 'Degree is required';
        }
        if (!qualification.yearOfPassing || qualification.yearOfPassing.toString().trim() === '') {
            errors.yearOfPassing = 'Year of passing is required';
        }
        if (!qualification.institution || qualification.institution.toString().trim() === '') {
            errors.institution = 'Institution is required';
        }
        if (!qualification.percentage || qualification.percentage.toString().trim() === '') {
            errors.percentage = 'Percentage/Grade/Class is required';
        }
        if (!qualification.specialization || qualification.specialization.toString().trim() === '') {
            errors.specialization = 'Specialization is required';
        }
        return errors;
    };

    // Validation function to check overall education validity
    const validateEducation = () => {
        const errors = {
            educationStatus: '',
            qualifications: []
        };

        // If not less than primary, must have at least one qualification with complete information
        if (!formData.lessThanPrimary) {
            if (!formData.qualifications || formData.qualifications.length === 0) {
                errors.educationStatus = 'Please either mark as less than primary education or add at least one qualification';
            } else {
                // Validate each qualification
                const qualificationErrors = formData.qualifications.map(qual => validateQualification(qual));
                errors.qualifications = qualificationErrors;
                
                // Check if at least one qualification has complete information
                const hasValidQualification = qualificationErrors.some(qualError => 
                    Object.keys(qualError).length === 0
                );
                
                if (!hasValidQualification) {
                    errors.educationStatus = 'Please provide complete information for at least one qualification';
                }
            }
        }

        setEducationErrors(errors);
        return Object.keys(errors.educationStatus).length === 0 && 
               errors.qualifications.every(qualError => Object.keys(qualError).length === 0);
    };

    const handleAddItem = useCallback(() => {
        setEmployeeData(prev => ({
            ...prev,
            qualifications: [
                ...(prev.qualifications || []),
                { 
                    degree: '', 
                    yearOfPassing: '', 
                    institution: '',
                    percentage: '',
                    specialization: ''
                },
            ],
        }));
        
        // Add empty error objects for new qualification
        setEducationErrors(prev => ({
            ...prev,
            qualifications: [...prev.qualifications, {}]
        }));
        setYearErrors(prev => ({
            ...prev,
            [formData.qualifications.length]: ''
        }));
    }, [setEmployeeData, formData.qualifications.length]);

    const handleRemoveItem = useCallback((index) => {
        setEmployeeData(prev => ({
            ...prev,
            qualifications: prev.qualifications.filter((_, i) => i !== index),
        }));
        
        // Remove corresponding errors
        setEducationErrors(prev => ({
            ...prev,
            qualifications: prev.qualifications.filter((_, i) => i !== index)
        }));
        setYearErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[index];
            return newErrors;
        });
    }, [setEmployeeData]);

    const handleSave = async () => {
        // Check if there are year validation errors
        if (Object.keys(yearErrors).length > 0) {
            toast.error('Please fix the validation errors before saving.', { position: 'top-right' });
            return { saved: false }; // Don't save if there are validation errors
        }
        
        // Validate education requirements
        if (!validateEducation()) {
            toast.error('Please complete the required education details before saving.', { position: 'top-right' });
            return { saved: false }; // Don't save if education validation fails
        }
        
        // Only send qualification section fields
        // If lessThanPrimary is checked, clear qualifications array when saving
        const sectionData = {
            lessThanPrimary: formData.lessThanPrimary || false,
            qualifications: formData.lessThanPrimary
                ? [] // Clear qualifications only when saving with lessThanPrimary checked
                : (formData.qualifications || []).map(qual => ({
                    degree: qual.degree || '',
                    yearOfPassing: qual.yearOfPassing || '',
                    institution: qual.institution || '',
                    percentage: qual.percentage || '',
                    specialization: qual.specialization || ''
                })),
        };
        
        try {
            const result = await onSaveSection(sectionData);
            // If lessThanPrimary is checked and save was successful, clear qualifications from state
            if (formData.lessThanPrimary && result) {
                setEmployeeData(prev => ({
                    ...prev,
                    qualifications: []
                }));
            }
            // Only navigate to next tab if save is successful
            if (onNext && !isLastTab) {
                onNext();
            }
            toast.success('Qualification details saved successfully', { position: 'top-right' });
            return { saved: true };
        } catch (error) {
            console.error('Save failed:', error);
            const msg =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                error?.message ||
                'Failed to save. Please try again.';
            toast.error(msg, { position: 'top-right' });
            return { saved: false };
        }
    };

    useEffect(() => {
        if (typeof onSaveTrigger === 'function') {
            onSaveTrigger(handleSave);
        }
    }, [onSaveTrigger, handleSave]);

    return (
        <div className="space-y-4">
            <fieldset disabled={readOnly} className={readOnly ? 'opacity-75' : ''}>
            <div className="flex items-center space-x-2 mb-4">
                <input
                    type="checkbox"
                    id="lessThanPrimary"
                    name="lessThanPrimary"
                    checked={formData.lessThanPrimary}
                    onChange={handleInputChange}
                    className="rounded border-gray-300 text-redhalo-navy shadow-sm focus:border-gray-300 focus:ring focus:ring-redhalo-red/25 focus:ring-opacity-50"
                />
                <label htmlFor="lessThanPrimary" className="text-sm font-medium text-gray-700">
                    Less than primary education
                </label>
            </div>
            {educationErrors.educationStatus && (
                <div className="text-red-500 text-sm mt-1">{educationErrors.educationStatus}</div>
            )}
            {!formData.lessThanPrimary && formData.qualifications.map((qualification, index) => (
                <div key={index} className="p-4 bg-white rounded-lg mb-4">
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                        <SearchableSelect
                            id={`degree-${index}`}
                            label="Degree"
                            value={qualification.degree}
                            onChange={(e) => handleNestedInputChange(index, 'degree', e.target.value)}
                            staticOptions={[
                                { value: '10th', label: '10th / Matriculation' },
                                { value: '12th', label: '12th / Higher Secondary' },
                                { value: 'diploma', label: 'Diploma' },
                                { value: 'graduation', label: "Graduation / Bachelor's Degree" },
                                { value: 'postGraduation', label: "Post Graduation / Master's Degree" },
                                { value: 'doctorate', label: 'Doctorate / Ph.D.' },
                                { value: 'professional', label: 'Professional Certification' },
                                { value: 'others', label: 'Others' },
                            ]}
                            required
                            error={educationErrors.qualifications[index]?.degree}
                        />
                        <FloatingInput
                            id={`yearOfPassing-${index}`}
                            label="Year of Completion"
                            type="number"
                            value={qualification.yearOfPassing}
                            onChange={(e) => handleNestedInputChange(index, 'yearOfPassing', e.target.value)}
                            required
                            error={yearErrors[index] || educationErrors.qualifications[index]?.yearOfPassing}
                            maxLength={4}
                            placeholder="e.g., 2023"
                        />
                        <FloatingInput
                            id={`institution-${index}`}
                            label="Name of the Institution"
                            value={qualification.institution}
                            onChange={(e) => handleNestedInputChange(index, 'institution', e.target.value)}
                            required
                            error={educationErrors.qualifications[index]?.institution}
                        />
                        <FloatingInput
                            id={`percentage-${index}`}
                            label="Percentage/Grade/Class"
                            type="text"
                            value={qualification.percentage}
                            onChange={(e) => handleNestedInputChange(index, 'percentage', e.target.value)}
                            required
                            error={educationErrors.qualifications[index]?.percentage}
                            placeholder="e.g., 85% or A+ or First Class"
                        />
                        <FloatingInput
                            id={`specialization-${index}`}
                            label="Specialization"
                            value={qualification.specialization}
                            onChange={(e) => handleNestedInputChange(index, 'specialization', e.target.value)}
                            required
                            error={educationErrors.qualifications[index]?.specialization}
                            placeholder="e.g., Computer Science, Engineering"
                        />
                    </div>
                    <button
                        onClick={() => handleRemoveItem(index)}
                        className="mt-2 text-red-600 hover:text-red-800"
                    >
                        Remove
                    </button>
                </div>
            ))}
            {!formData.lessThanPrimary && (
            <button
                onClick={handleAddItem}
                className="m-2 text-redhalo-navy hover:text-redhalo-navy-dark"
            >
                <PlusCircle className="inline-block mr-1" size={16} /> Add Education
            </button>
            )}
            </fieldset>
           {feedback && (
  <div className={`mt-2 ${feedback.type === 'success' ? 'text-emerald-600' : 'text-red-600'}`}>
    {typeof feedback.message === 'string'
      ? feedback.message
      : JSON.stringify(feedback.message)}
  </div>
)}

        </div>
    );
}